import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { buttonVariants } from '@/components/ui/button'
import { SAMPLE_TRIP_CODE } from '@/constants/trip'

const NAV_LINKS = [
    { href: '/#how-it-works', label: 'How it works' },
    { href: '/#try-it', label: 'Try it' },
    { href: '/#features', label: 'Features' },
    { href: `/trips/${SAMPLE_TRIP_CODE}`, label: 'Sample trip' },
    { href: '/#faq', label: 'FAQ' }
]

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const close = () => setOpen(false)
        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    return (
        <header id='site-header' className='sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md'>
            <div className='mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6'>
                <Link href='/' className='rounded-lg outline-none focus-visible:ring-3 focus-visible:ring-ring/50' aria-label='Tallyho home'>
                    <Logo />
                </Link>

                <nav aria-label='Main' className='hidden items-center gap-1 md:flex'>
                    {NAV_LINKS.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className='rounded-full px-3.5 py-2 text-sm font-semibold text-foreground/70 transition-colors hover:bg-muted hover:text-foreground'
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <Link href='/trips/new' className={cn(buttonVariants({ size: 'sm' }), 'hidden h-9 px-4 sm:inline-flex')}>
                        Start a trip
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-nav'
                        className='inline-flex size-10 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted md:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}

                        <span className='sr-only'>
                            {open ? 'Close menu' : 'Open menu'}
                        </span>
                    </button>
                </div>
            </div>

            {open && (
                <nav
                    id='mobile-nav'
                    aria-label='Mobile'
                    className='animate-in fade-in slide-in-from-top-2 border-t border-border/70 bg-background px-4 pt-2 pb-4 duration-200 md:hidden'
                >
                    <ul className='flex flex-col'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='block rounded-xl px-3 py-3 text-base font-semibold text-foreground/80 hover:bg-muted'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <Link
                        href='/trips/new'
                        onClick={() => setOpen(false)}
                        className={cn(buttonVariants({ size: 'lg' }), 'mt-3 w-full')}
                    >
                        Start a trip
                    </Link>
                </nav>
            )}
        </header>
    )
}
